import { useEffect, useRef, useState } from 'react';
import { NavHeader, type NavView } from '../design-system/NavHeader';
import { Alert } from '../design-system/Alert';
import { Tooltip } from '../design-system/Tooltip';
import { useLanguage } from '../context/LanguageContext';
import {
  clearMomentumLogs,
  fetchEngineControlStatus,
  fetchMomentumDiagnostics,
  fetchMomentumLogs,
  fetchTopMovers,
  saveMomentumSourcesConfig,
  triggerMomentumScan,
  type EngineControlStatus,
  type MomentumDiagnostics,
  type MomentumLogEntry,
  type MomentumSourcesConfig,
  type SentimentMover,
} from '../services/sentimentMoversService';
import styles from './TopMovers.module.css';

const LOGS_POLL_INTERVAL_MS = 5000;

interface Props {
  serverUrl: string;
  onNavigate: (view: NavView) => void;
}

const SOURCES_STORAGE_KEY = 'gitextrade_momentum_sources';

const EMPTY_SOURCES: MomentumSourcesConfig = {
  redditClientId: '',
  redditClientSecret: '',
  twitterBearerToken: '',
  finnhubApiKey: '',
};

function loadStoredSources(): MomentumSourcesConfig {
  try {
    const raw = localStorage.getItem(SOURCES_STORAGE_KEY);
    if (!raw) return EMPTY_SOURCES;
    const parsed: Partial<MomentumSourcesConfig> = JSON.parse(raw);
    return { ...EMPTY_SOURCES, ...parsed };
  } catch {
    return EMPTY_SOURCES;
  }
}

// Mirrors the engine's default momentum_score_threshold (0.6, see
// StrategyParams in engine/src/gitextrade/types.py) as the boundary between
// "borderline" and "would trigger a long entry" — below 0.4 is unambiguously
// too weak to matter.
export function momentumScoreClassName(score: number): string {
  if (score >= 0.6) return styles.scoreHigh;
  if (score >= 0.4) return styles.scoreMid;
  return styles.scoreLow;
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }): JSX.Element {
  return (
    <span className={ok ? styles.badgeOk : styles.badgeFail}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );
}

export function TopMovers({ serverUrl, onNavigate }: Props): JSX.Element {
  const { t } = useLanguage();
  const [movers, setMovers] = useState<SentimentMover[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [diagnostics, setDiagnostics] = useState<MomentumDiagnostics | null>(null);
  const [diagnosticsFailed, setDiagnosticsFailed] = useState(false);

  const [sources, setSources] = useState<MomentumSourcesConfig>(loadStoredSources);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<'success' | 'error' | null>(null);

  const [logs, setLogs] = useState<MomentumLogEntry[]>([]);
  const [logsFailed, setLogsFailed] = useState(false);
  const consoleRef = useRef<HTMLDivElement>(null);

  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<'error' | null>(null);

  const [engineStatus, setEngineStatus] = useState<EngineControlStatus | null>(null);
  const [engineStatusFailed, setEngineStatusFailed] = useState(false);

  const loadMovers = async (): Promise<void> => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await fetchTopMovers(serverUrl);
      setMovers(data);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const loadDiagnostics = async (): Promise<void> => {
    setDiagnosticsFailed(false);
    try {
      const data = await fetchMomentumDiagnostics(serverUrl);
      setDiagnostics(data);
    } catch {
      setDiagnosticsFailed(true);
    }
  };

  const loadLogs = async (): Promise<void> => {
    try {
      const data = await fetchMomentumLogs(serverUrl);
      setLogs(data);
      setLogsFailed(false);
    } catch {
      setLogsFailed(true);
    }
  };

  const loadEngineStatus = async (): Promise<void> => {
    try {
      const data = await fetchEngineControlStatus(serverUrl);
      setEngineStatus(data);
      setEngineStatusFailed(false);
    } catch {
      setEngineStatusFailed(true);
    }
  };

  useEffect(() => {
    void loadMovers();
    void loadDiagnostics();
    void loadLogs();
    void loadEngineStatus();
    const interval = setInterval(() => {
      void loadLogs();
      void loadEngineStatus();
    }, LOGS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [serverUrl]);

  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs]);

  const handleSaveSources = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      await saveMomentumSourcesConfig(serverUrl, sources);
      localStorage.setItem(SOURCES_STORAGE_KEY, JSON.stringify(sources));
      setSaveMsg('success');
      await loadDiagnostics();
    } catch {
      setSaveMsg('error');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerScan = async (): Promise<void> => {
    setScanning(true);
    setScanMsg(null);
    try {
      await triggerMomentumScan(serverUrl);
      await Promise.all([loadMovers(), loadDiagnostics(), loadLogs()]);
    } catch {
      setScanMsg('error');
    } finally {
      setScanning(false);
    }
  };

  const handleClearLogs = async (): Promise<void> => {
    try {
      await clearMomentumLogs(serverUrl);
      setLogs([]);
    } catch {
      // logError already reported it inside clearMomentumLogs; the console
      // stays as-is rather than showing a separate error banner for a
      // low-stakes UI declutter action.
    }
  };

  const lastScanLabel = diagnostics?.lastScanAt
    ? new Date(diagnostics.lastScanAt * 1000).toLocaleTimeString()
    : t('topMovers.diag.never');

  return (
    <>
      <NavHeader active="momentum" onNavigate={onNavigate} />
      <main style={{ padding: 'var(--space-6)' }}>
        <div className={styles.panel}>
          <div className={styles.headerRow}>
            <h2 className={styles.panelTitle}>{t('topMovers.title')}</h2>
            <div className={styles.headerActions}>
              {diagnostics && (
                <span
                  className={diagnostics.marketOpen ? styles.marketOpen : styles.marketClosed}
                >
                  {diagnostics.marketOpen
                    ? t('topMovers.marketOpen')
                    : t('topMovers.marketClosed')}
                </span>
              )}
              <button
                type="button"
                className={styles.saveBtn}
                onClick={() => void handleTriggerScan()}
                disabled={scanning}
              >
                {scanning ? t('topMovers.scanRunning') : t('topMovers.scanNow')}
              </button>
            </div>
          </div>
          {scanMsg === 'error' && (
            <Alert variant="danger" onDismiss={() => setScanMsg(null)}>
              {t('topMovers.scanError')}
            </Alert>
          )}

          {/* Control lights: is the engine even running, and in this mode? */}
          <div className={styles.badgeRow}>
            <StatusBadge
              ok={engineStatus?.botActive ?? false}
              label={t('topMovers.engine.active')}
            />
            <StatusBadge
              ok={engineStatus?.strategyMode === 'sentiment_momentum'}
              label={
                engineStatus?.strategyMode === 'sentiment_momentum'
                  ? t('topMovers.engine.modeActive')
                  : `${t('topMovers.engine.modeInactive')}: ${engineStatus?.strategyMode ?? '—'}`
              }
            />
          </div>
          {engineStatusFailed && <Alert variant="danger">{t('topMovers.engine.error')}</Alert>}

          {/* Diagnostics: which component isn't working */}
          <div className={styles.diagCard}>
            <h3 className={styles.diagTitle}>{t('topMovers.diag.title')}</h3>
            {diagnosticsFailed && (
              <Alert variant="danger">{t('topMovers.diag.error')}</Alert>
            )}
            {diagnostics && !diagnosticsFailed && (
              <>
                <div className={styles.badgeRow}>
                  <StatusBadge ok={diagnostics.redditConfigured} label={t('topMovers.diag.reddit')} />
                  <StatusBadge
                    ok={diagnostics.twitterConfigured}
                    label={t('topMovers.diag.twitter')}
                  />
                  <StatusBadge
                    ok={diagnostics.finnhubConfigured}
                    label={t('topMovers.diag.finnhub')}
                  />
                  <StatusBadge
                    ok={diagnostics.tradingviewOk}
                    label={t('topMovers.diag.tradingview')}
                  />
                </div>
                <dl className={styles.diagStats}>
                  <div>
                    <dt>{t('topMovers.diag.lastScan')}</dt>
                    <dd>{lastScanLabel}</dd>
                  </div>
                  <div>
                    <dt>{t('topMovers.diag.candidates')}</dt>
                    <dd>{diagnostics.candidateCount}</dd>
                  </div>
                  <div>
                    <dt>{t('topMovers.diag.mentionsReddit')}</dt>
                    <dd>{diagnostics.redditMentionsTotal}</dd>
                  </div>
                  <div>
                    <dt>{t('topMovers.diag.mentionsTwitter')}</dt>
                    <dd>{diagnostics.twitterMentionsTotal}</dd>
                  </div>
                  <div>
                    <dt>{t('topMovers.diag.mentionsNews')}</dt>
                    <dd>{diagnostics.newsMentionsTotal}</dd>
                  </div>
                  <div>
                    <dt>{t('topMovers.diag.topMoversFound')}</dt>
                    <dd>{diagnostics.topMoversCount}</dd>
                  </div>
                </dl>
              </>
            )}
          </div>

          {/* Ranked NASDAQ momentum movers */}
          {loading && <div className={styles.stateMsg}>{t('topMovers.loading')}</div>}
          {failed && !loading && (
            <div style={{ marginBottom: 'var(--space-8)' }}>
              <Alert variant="danger">{t('topMovers.error')}</Alert>
            </div>
          )}

          {!loading && !failed && movers.length === 0 && (
            <div className={styles.stateMsg}>{t('topMovers.empty')}</div>
          )}

          {!loading && !failed && movers.length > 0 && (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('topMovers.rank')}</th>
                    <th>{t('topMovers.symbol')}</th>
                    <th style={{ textAlign: 'right' }}>
                      {t('topMovers.momentumScore')}{' '}
                      <Tooltip translationKey="tooltip.momentumScore" />
                    </th>
                    <th style={{ textAlign: 'right' }}>{t('topMovers.mentionCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {movers.map((mover, index) => (
                    <tr key={mover.symbol}>
                      <td>{index + 1}</td>
                      <td className={styles.symbolCell}>{mover.symbol}</td>
                      <td
                        className={`${styles.numericCell} ${momentumScoreClassName(mover.momentumScore)}`}
                      >
                        {mover.momentumScore.toFixed(2)}
                      </td>
                      <td className={styles.numericCell}>{mover.mentionCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Data source keys */}
          <div className={styles.configCard}>
            <h3 className={styles.diagTitle}>{t('topMovers.config.title')}</h3>
            <form onSubmit={handleSaveSources} className={styles.configForm}>
              <div className={styles.fieldGroup}>
                <label htmlFor="redditClientId">{t('topMovers.config.redditClientId')}</label>
                <input
                  id="redditClientId"
                  type="text"
                  className={styles.input}
                  value={sources.redditClientId}
                  onChange={(e) => setSources({ ...sources, redditClientId: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="redditClientSecret">
                  {t('topMovers.config.redditClientSecret')}
                </label>
                <input
                  id="redditClientSecret"
                  // Plain text, not type="password": these are API tokens, not
                  // login credentials, and browser password managers (Chrome
                  // especially) can silently clear/replace password-type
                  // fields via autofill heuristics when several sit in one
                  // form — that can wipe a typed key right before submit.
                  type="text"
                  className={styles.input}
                  value={sources.redditClientSecret}
                  onChange={(e) =>
                    setSources({ ...sources, redditClientSecret: e.target.value })
                  }
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="twitterBearerToken">
                  {t('topMovers.config.twitterBearerToken')}
                </label>
                <input
                  id="twitterBearerToken"
                  type="text"
                  className={styles.input}
                  value={sources.twitterBearerToken}
                  onChange={(e) =>
                    setSources({ ...sources, twitterBearerToken: e.target.value })
                  }
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="finnhubApiKey">{t('topMovers.config.finnhubApiKey')}</label>
                <input
                  id="finnhubApiKey"
                  type="text"
                  className={styles.input}
                  value={sources.finnhubApiKey}
                  onChange={(e) => setSources({ ...sources, finnhubApiKey: e.target.value })}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
              <button type="submit" className={styles.saveBtn} disabled={saving}>
                {saving ? t('topMovers.config.saving') : t('topMovers.config.save')}
              </button>
            </form>
            {saveMsg === 'success' && (
              <Alert variant="success" onDismiss={() => setSaveMsg(null)}>
                {t('topMovers.config.saved')}
              </Alert>
            )}
            {saveMsg === 'error' && (
              <Alert variant="danger" onDismiss={() => setSaveMsg(null)}>
                {t('topMovers.config.saveError')}
              </Alert>
            )}
          </div>

          {/* Live console: what the scanner is actually doing */}
          <div className={styles.diagCard}>
            <div className={styles.consoleHeaderRow}>
              <h3 className={styles.diagTitle}>{t('topMovers.console.title')}</h3>
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => void handleClearLogs()}
                disabled={logs.length === 0}
              >
                {t('topMovers.console.clear')}
              </button>
            </div>
            {logsFailed && <Alert variant="danger">{t('topMovers.console.error')}</Alert>}
            <div className={styles.console} ref={consoleRef}>
              {logs.length === 0 && !logsFailed && (
                <div className={styles.consoleEmpty}>{t('topMovers.console.empty')}</div>
              )}
              {logs.map((entry, i) => (
                <div
                  key={i}
                  className={
                    entry.level === 'error'
                      ? styles.consoleLineError
                      : entry.level === 'warning'
                        ? styles.consoleLineWarning
                        : styles.consoleLineInfo
                  }
                >
                  <span className={styles.consoleTimestamp}>
                    {new Date(entry.timestamp * 1000).toLocaleTimeString()}
                  </span>{' '}
                  {entry.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
